import { ActionFunction } from 'react-router-dom';
import { client } from '~/api/client';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import type { DataResult } from '~/api/data/result';
import {
  DeleteMemoryDocument,
  DeleteMemoryMutation,
  DeleteMemoryMutationVariables,
  DemoteMemoryDocument,
  DemoteMemoryMutation,
  DemoteMemoryMutationVariables,
  PromoteMemoryDocument,
  PromoteMemoryMutation,
  PromoteMemoryMutationVariables,
  UpdateMemoryDocument,
  UpdateMemoryMutation,
  UpdateMemoryMutationVariables
} from '~/api/graphql';

export const updateMemory = (variables: UpdateMemoryMutationVariables) =>
  mutateData<UpdateMemoryMutation, UpdateMemoryMutationVariables>(client, {
    mutation: UpdateMemoryDocument,
    variables
  });

export const deleteMemory = (variables: DeleteMemoryMutationVariables) =>
  mutateData<DeleteMemoryMutation, DeleteMemoryMutationVariables>(client, {
    mutation: DeleteMemoryDocument,
    variables
  });

export const promoteMemory = (variables: PromoteMemoryMutationVariables) =>
  mutateData<PromoteMemoryMutation, PromoteMemoryMutationVariables>(client, {
    mutation: PromoteMemoryDocument,
    variables
  });

export const demoteMemory = (variables: DemoteMemoryMutationVariables) =>
  mutateData<DemoteMemoryMutation, DemoteMemoryMutationVariables>(client, {
    mutation: DemoteMemoryDocument,
    variables
  });

// Compose the authorized per-memory mutations so ownership and organization
// permissions remain enforced for each record; retain failed IDs for retries.
const bulkMutateMemories = async (
  memoryIds: string[],
  mutate: (id: string) => Promise<DataResult<unknown>>
) => {
  const ids = [...new Set(memoryIds ?? [])];
  const results = await Promise.all(
    ids.map(async (id) => {
      try {
        const result = await mutate(id);
        return { id, ok: !result.error };
      } catch {
        return { id, ok: false };
      }
    })
  );
  return { failedIds: results.filter((result) => !result.ok).map((result) => result.id) };
};

export const bulkPinMemories = ({ memoryIds }: { memoryIds: string[] }) =>
  bulkMutateMemories(memoryIds, (id) => updateMemory({ input: { id, sticky: true } }));

export const bulkDeleteMemories = ({ memoryIds }: { memoryIds: string[] }) =>
  bulkMutateMemories(memoryIds, (id) => deleteMemory({ id }));

export const bulkPromoteMemories = ({ memoryIds }: { memoryIds: string[] }) =>
  bulkMutateMemories(memoryIds, (id) => promoteMemory({ id }));

export const bulkDemoteMemories = ({ memoryIds }: { memoryIds: string[] }) =>
  bulkMutateMemories(memoryIds, (id) => demoteMemory({ id }));

export const submitUpdateMemory: ActionFunction = async ({ request }) => {
  const input = await request.json();
  return submitMutation(updateMemory({ input }));
};

export const submitBulkPinMemories: ActionFunction = async ({ request }) =>
  bulkPinMemories(await request.json());

export const submitBulkDeleteMemories: ActionFunction = async ({ request }) =>
  bulkDeleteMemories(await request.json());

export const submitBulkPromoteMemories: ActionFunction = async ({ request }) =>
  bulkPromoteMemories(await request.json());

export const submitBulkDemoteMemories: ActionFunction = async ({ request }) =>
  bulkDemoteMemories(await request.json());
